import axios from 'axios';

// Get API base URL from runtime config (for production) or environment variable (for development)
const getApiBaseUrl = (): string => {
  // Check for runtime config (set in public/runtime-config.js, useful for production)
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.API_BASE_URL) {
    return (window as any).RUNTIME_CONFIG.API_BASE_URL;
  }
  // Fall back to environment variable
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // Default fallback for local development
  return 'http://localhost:12312/api';
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 120000, // 2 minutes for large uploads
  headers: {
    'Content-Type': 'application/json'
  },
});

// Track request start times for duration calculation
const requestStartTimes = new Map<string, number>();

// Add a request interceptor to include the JWT token and log requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Log request and track start time
  const requestId = `${config.method}-${config.url}-${Date.now()}`;
  requestStartTimes.set(requestId, Date.now());
  (config as any)._requestId = requestId;
  
  return config;
});

// Global lock to prevent multiple simultaneous redirects from different 401 errors
let pendingRedirect: Promise<void> | null = null;

// Add a response interceptor to handle 401 errors globally
// This handles in-app token refresh when API calls fail due to expired tokens
apiClient.interceptors.response.use(
  (response) => {
    // Log successful response
    const requestId = (response.config as any)?._requestId;
    if (requestId) {
      requestStartTimes.delete(requestId);
      
        // Logging removed - fetch logger was deleted
    }
    return response;
  },
  async (error) => {
    // Clean up request tracking
    const requestId = (error.config as any)?._requestId;
    if (requestId) {
      requestStartTimes.delete(requestId);
    }
    const originalRequest = error.config;

    // If we get a 401 and haven't already retried, check for redirect URL or attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if 401 response contains redirect URL in response body
      const responseData = error.response?.data || {};
      
      const redirectUrl = 
        responseData.oauth_url ||
        (responseData.detail && typeof responseData.detail === 'object' && responseData.detail.oauth_url) ||
        (responseData.message && typeof responseData.message === 'object' && responseData.message.oauth_url) ||
        error.oauth_url;
      
      if (redirectUrl) {
        // Redirect URL provided - redirect to OAuth flow
        // Use global lock to prevent multiple redirects
        if (!pendingRedirect) {
          const { redirectToOAuth } = await import('../utils/redirectHelper.js');
          pendingRedirect = redirectToOAuth(redirectUrl).then(() => {
            // Clear the lock after a short delay
            setTimeout(() => {
              pendingRedirect = null;
            }, 1000);
          }).catch(() => {
            pendingRedirect = null;
          });
        }
        return Promise.reject(error);
      }

      // No redirect URL - attempt token refresh
      try {
        // Import dynamically to avoid circular dependencies
        const { handleGoogleRefreshFlow } = await import('../utils/oauthHelper.js');
        await handleGoogleRefreshFlow();
        
        // Retry the original request with new token
        const token = localStorage.getItem('jwt');
        if (token) {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        // Check if refresh error contains redirect URL (from handleGoogleRefreshFlow or response)
        const refreshResponseData = refreshError?.response?.data || {};
        const refreshRedirectUrl = 
          refreshError?.oauth_url ||
          refreshResponseData.oauth_url ||
          (refreshResponseData.detail && typeof refreshResponseData.detail === 'object' && refreshResponseData.detail.oauth_url) ||
          (refreshResponseData.message && typeof refreshResponseData.message === 'object' && refreshResponseData.message.oauth_url);
        
        if (refreshRedirectUrl) {
          // Refresh returned redirect - redirect to OAuth flow
          // Use global lock to prevent multiple redirects
          if (!pendingRedirect) {
            const { redirectToOAuth } = await import('../utils/redirectHelper.js');
            pendingRedirect = redirectToOAuth(refreshRedirectUrl).then(() => {
              // Clear the lock after a short delay
              setTimeout(() => {
                pendingRedirect = null;
              }, 1000);
            }).catch(() => {
              pendingRedirect = null;
            });
          }
          return Promise.reject(refreshError);
        }
        
        // Refresh failed without redirect - clear storage and redirect to login
        const { clearStorage } = await import('../utils/localStorageManager.js');
        clearStorage();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;